import { z } from 'zod';
import { normalizePlanRotation } from './planGeometry';

import { anchorToItem, anchorToPoint } from './dimensions';
import { computeBackgroundHash } from './metrics';
import {
  LINE_COLORS,
  type Anchor,
  type BgTransform,
  type CustomBlockPayload,
  type Dimension,
  type Line,
  type MechanismType,
  type PlacedItem,
  type PlanCalibration,
} from './types';

export const ALLOWED_TYPES = [
  'lighting_single',
  'exhaust_fan',
  'lighting_spot',
  'switch',
  'switch_double',
  'master_switch',
  'socket_single',
  'socket_double',
  'socket_triple',
  'socket_quadruple',
  'bathroom_socket',
  'kitchen_socket',
  'tv_socket',
  'rj45_socket',
  'washing_machine',
  'oven',
  'hob',
  'microwave',
  'extractor_hood',
  'refrigerator',
  'freezer',
  'air_conditioner',
  'boiler',
  'dryer',
  'electric_heater',
  'ev_charger',
  'hydromassage_bath',
  'dishwasher',
  'automation',
  'custom_block',
] as const;

export type AllowedType = typeof ALLOWED_TYPES[number];

export interface PlanProjectFile {
  format: 'panelforge-plan';
  version: 1;
  plan: {
    items: PlacedItem[];
    lines: Line[];
    activeLineId: string;
    iconSize: number;
    gridEnabled: boolean;
    gridSize: number;
    bgTransform: BgTransform;
    background: string | null;
    backgroundHash: string | null;
    calibration: PlanCalibration | null;
    dimensions: Dimension[];
  };
}

export type PlanProject = PlanProjectFile['plan'];

const allowedTypes = new Set<string>(ALLOWED_TYPES);
const allowedMechanismTypes = new Set<MechanismType>([
  'socket',
  'bathroom_socket',
  'kitchen_socket',
  'switch',
  'switch_double',
  'internet',
  'tv',
  'usb',
]);
const lineColors = new Set<string>(LINE_COLORS);
const DATA_URL_PREFIXES = ['data:image/png;base64,', 'data:image/jpeg;base64,'];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function trimmedText(value: unknown, maxLength: number): string | undefined {
  const text = stringValue(value)?.trim();
  if (!text) return undefined;
  return text.slice(0, maxLength);
}

function objectValue(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function normalizeIconSize(value: unknown): number {
  const size = finiteNumber(value);
  return clamp(size ?? 36, 12, 60);
}

function normalizeGridSize(value: unknown): number {
  const size = finiteNumber(value);
  return clamp(size ?? 40, 20, 80);
}

function normalizeBgTransform(value: unknown): BgTransform {
  const input = objectValue(value);
  return {
    x: finiteNumber(input.x) ?? 0,
    y: finiteNumber(input.y) ?? 0,
    scale: clamp(finiteNumber(input.scale) ?? 1, 0.3, 2.5),
    rotation: normalizePlanRotation(input.rotation),
  };
}

function normalizeBackground(value: unknown): string | null {
  const dataUrl = stringValue(value);
  if (!dataUrl) return null;
  return DATA_URL_PREFIXES.some(prefix => dataUrl.startsWith(prefix)) ? dataUrl : null;
}

function normalizeBackgroundHash(value: unknown, background: string | null): string | null {
  const hash = stringValue(value)?.trim();
  if (hash) return hash.slice(0, 120);
  return background === null ? null : computeBackgroundHash(background);
}

function normalizeCalibration(value: unknown): PlanCalibration | null {
  const input = objectValue(value);
  const pxPerMm = finiteNumber(input.pxPerMm);
  const refLengthMm = finiteNumber(input.refLengthMm);
  const backgroundHash = stringValue(input.backgroundHash)?.trim();
  const createdAt = stringValue(input.createdAt)?.trim();
  const segment = objectValue(input.refSegmentSourcePx);
  const x1 = finiteNumber(segment.x1);
  const y1 = finiteNumber(segment.y1);
  const x2 = finiteNumber(segment.x2);
  const y2 = finiteNumber(segment.y2);

  if (
    pxPerMm === null || pxPerMm <= 0 ||
    refLengthMm === null || refLengthMm <= 0 ||
    !backgroundHash ||
    input.confidence !== 'user' ||
    !createdAt ||
    x1 === null || y1 === null || x2 === null || y2 === null
  ) {
    return null;
  }

  return {
    pxPerMm,
    refSegmentSourcePx: { x1, y1, x2, y2 },
    refLengthMm,
    backgroundHash: backgroundHash.slice(0, 120),
    confidence: 'user',
    createdAt: createdAt.slice(0, 80),
  };
}

function normalizeAnchor(value: unknown): Anchor | null {
  const input = objectValue(value);
  const itemId = stringValue(input.itemId)?.trim();
  if (itemId) return anchorToItem(itemId.slice(0, 120));

  const point = objectValue(input.pointSourcePx);
  const x = finiteNumber(point.x);
  const y = finiteNumber(point.y);
  return x === null || y === null ? null : anchorToPoint({ x, y });
}

function normalizeDimensions(value: unknown): Dimension[] {
  if (!Array.isArray(value)) return [];

  const dimensions: Dimension[] = [];
  const seen = new Set<string>();
  for (const raw of value.slice(0, 500)) {
    const input = objectValue(raw);
    const id = stringValue(input.id)?.trim() || crypto.randomUUID();
    if (seen.has(id)) continue;

    const a = normalizeAnchor(input.a);
    const b = normalizeAnchor(input.b);
    const offsetPx = finiteNumber(input.offsetPx);
    if (!a || !b || offsetPx === null) continue;

    const labelOverride = input.labelOverride === null
      ? null
      : trimmedText(input.labelOverride, 120) ?? null;

    dimensions.push({
      id: id.slice(0, 120),
      a,
      b,
      offsetPx,
      labelOverride,
      locked: typeof input.locked === 'boolean' ? input.locked : false,
    });
    seen.add(id);
  }
  return dimensions;
}

function normalizeRotation(value: unknown): PlacedItem['rotation'] {
  return value === 90 || value === 180 || value === 270 ? value : 0;
}

function normalizeSwitchLineId(value: unknown, lineIds: Set<string>): string | null {
  const lineId = value === null ? null : stringValue(value);
  return lineId && lineIds.has(lineId) ? lineId : null;
}

function normalizeSwitchLines(value: unknown, lineIds: Set<string>): PlacedItem['switchLines'] {
  const input = objectValue(value);
  return {
    left: normalizeSwitchLineId(input.left, lineIds),
    right: normalizeSwitchLineId(input.right, lineIds),
  };
}

function normalizeMasterMode(value: unknown): PlacedItem['masterMode'] {
  return value === 'realistic' ? 'realistic' : 'simple';
}

function normalizeMechanismType(value: unknown): MechanismType | null {
  const type = stringValue(value);
  if (type === 'switch_1') return 'switch';
  if (type === 'switch_2') return 'switch_double';
  return type && allowedMechanismTypes.has(type as MechanismType) ? type as MechanismType : null;
}

function normalizeCustomBlockPayload(value: unknown, lineIds: Set<string>): CustomBlockPayload | undefined {
  const input = objectValue(value);
  if (input.version !== 1) return undefined;
  if (input.orientation !== 'horizontal' && input.orientation !== 'vertical') return undefined;
  if (!Array.isArray(input.mechanisms)) return undefined;

  const mechanisms = input.mechanisms.flatMap(raw => {
    const mechanism = objectValue(raw);
    const type = normalizeMechanismType(mechanism.type);
    if (!type) return [];

    const label = trimmedText(mechanism.label, 40);
    const rawLineId = stringValue(mechanism.lineId);
    const lineId = type === 'switch' && rawLineId && lineIds.has(rawLineId) ? rawLineId : null;
    return [{
      type,
      ...(label ? { label } : {}),
      ...(lineId ? { lineId } : {}),
    }];
  }).slice(0, 4);

  if (mechanisms.length === 0) return undefined;

  const label = trimmedText(input.label, 80);
  return {
    version: 1,
    ...(label ? { label } : {}),
    orientation: input.orientation,
    mechanisms,
  };
}

function normalizeLines(value: unknown): Line[] {
  if (!Array.isArray(value)) return [];

  const lines: Line[] = [];
  const seen = new Set<string>();

  for (const raw of value.slice(0, 8)) {
    const input = objectValue(raw);
    const id = stringValue(input.id)?.trim();
    if (!id || seen.has(id)) continue;

    const name = stringValue(input.name) ?? id;
    const color = stringValue(input.color);
    lines.push({
      id,
      name: name.slice(0, 100),
      color: color && lineColors.has(color) ? color : LINE_COLORS[0],
    });
    seen.add(id);
  }

  return lines;
}

function normalizeItems(value: unknown, lines: Line[]): PlacedItem[] {
  if (!Array.isArray(value)) return [];

  const lineIds = new Set(lines.map(line => line.id));
  const items: PlacedItem[] = [];

  for (const raw of value.slice(0, 500)) {
    const input = objectValue(raw);
    const type = stringValue(input.type);
    if (!type || !allowedTypes.has(type)) continue;

    const u = finiteNumber(input.u);
    const v = finiteNumber(input.v);
    if (u === null || v === null) continue;

    const rawId = stringValue(input.id)?.trim();
    const rawLineId = input.lineId === null ? null : stringValue(input.lineId);
    const lineId = rawLineId && lineIds.has(rawLineId) ? rawLineId : null;

    const customBlock = type === 'custom_block'
      ? normalizeCustomBlockPayload(input.customBlock, lineIds)
      : undefined;
    const blockId = trimmedText(input.blockId, 80);

    items.push({
      id: rawId || crypto.randomUUID(),
      type,
      u: clamp(u, -1, 2),
      v: clamp(v, -1, 2),
      lineId: type === 'switch_double' ? null : lineId,
      rotation: normalizeRotation(input.rotation),
      blockId,
      customBlock,
      switchLines: type === 'switch_double' ? normalizeSwitchLines(input.switchLines, lineIds) : undefined,
      masterMode: type === 'master_switch' ? normalizeMasterMode(input.masterMode) : undefined,
    });
  }

  return items;
}

function normalizeActiveLineId(value: unknown, lines: Line[]): string {
  const activeLineId = stringValue(value);
  if (activeLineId && lines.some(line => line.id === activeLineId)) return activeLineId;
  return lines[0]?.id ?? 'L1';
}

export const rawSchema = z.object({
  format: z.literal('panelforge-plan'),
  version: z.literal(1),
  plan: z.unknown(),
}).transform((raw): PlanProjectFile => {
  const input = objectValue(raw.plan);
  const lines = normalizeLines(input.lines);
  const background = normalizeBackground(input.background);

  return {
    format: 'panelforge-plan',
    version: 1,
    plan: {
      items: normalizeItems(input.items, lines),
      lines,
      activeLineId: normalizeActiveLineId(input.activeLineId, lines),
      iconSize: normalizeIconSize(input.iconSize),
      gridEnabled: typeof input.gridEnabled === 'boolean' ? input.gridEnabled : false,
      gridSize: normalizeGridSize(input.gridSize),
      bgTransform: normalizeBgTransform(input.bgTransform),
      background,
      backgroundHash: normalizeBackgroundHash(input.backgroundHash, background),
      calibration: normalizeCalibration(input.calibration),
      dimensions: normalizeDimensions(input.dimensions),
    },
  };
});

export function parseProjectFile(
  jsonText: string,
): { ok: true; project: PlanProject } | { ok: false; error: string } {
  let data: unknown;
  try {
    data = JSON.parse(jsonText);
  } catch {
    return { ok: false, error: 'invalid JSON' };
  }

  const result = rawSchema.safeParse(data);
  if (!result.success) {
    return { ok: false, error: 'not a panelforge plan file' };
  }

  return { ok: true, project: result.data.plan };
}

export function serializeProject(plan: PlanProject): string {
  const projectFile: PlanProjectFile = {
    format: 'panelforge-plan',
    version: 1,
    plan,
  };
  return JSON.stringify(projectFile);
}

export async function validateBackgroundDataUrl(dataUrl: string): Promise<boolean> {
  let bmp: ImageBitmap | null = null;
  try {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    bmp = await createImageBitmap(blob);
    return true;
  } catch {
    return false;
  } finally {
    bmp?.close();
  }
}
